import React from 'react';
import GenericTagPicker, { TagPickerItem } from './PickerComponent/GenericTagPicker';
import { 
    carbonCopyContactPrepopulate, 
    searchCarbonCopyContacts, 
    CarbonCopyContactResult 
} from '../../../services/api/dataServiceOutlookQuickSave';

interface CarbonCopyPickerProps {
    selectedContacts: CarbonCopyContactResult[];
    disabled?: boolean;
    onContactsChange: (contacts: CarbonCopyContactResult[]) => void;
    label?: string;
    placeholder?: string;
    caseNumber?: number; // Add caseNumber prop
}

// Interface extending TagPickerItem to include contact data
interface ContactTagPickerItem extends TagPickerItem {
    contact: CarbonCopyContactResult;
}

// Helper function to convert CarbonCopyContactResult to TagPickerItem
const adaptContactToTagPickerItem = (contact: CarbonCopyContactResult): ContactTagPickerItem => {
    const displayName = contact.EmployerName
        ? `${contact.Name} (${contact.EmployerName})`
        : contact.Name;
    
    return {
        id: contact.Recno,
        displayText: displayName,
        contact
    };
};

// Helper function to convert TagPickerItem back to CarbonCopyContactResult
const adaptTagPickerItemToContact = (item: TagPickerItem): CarbonCopyContactResult => {
    return (item as ContactTagPickerItem).contact;
};

// Search function for carbon copy contacts
const searchContacts = async (searchText: string): Promise<ContactTagPickerItem[]> => {
    const normalizedSearchText = normalizeSearchText(searchText);
    const contacts = await searchCarbonCopyContacts(normalizedSearchText);
    return contacts.map(adaptContactToTagPickerItem);
};

// Helper function to normalize search text - reduces cognitive complexity
const normalizeSearchText = (searchText: string): string => {
    const trimmedSearchText = searchText.trim();
    
    if (trimmedSearchText === '%' || trimmedSearchText === '*') {
        return '*';  // Pure wildcard search - don't append anything
    }
    
    if (trimmedSearchText === '') {
        return '*'; // Search for all if empty
    }
    
    return `${trimmedSearchText}*`; // Append * to regular search text
};

// Prepopulate function for carbon copy contacts - only runs when caseNumber is provided
const prepopulateContacts = async (context?: number): Promise<ContactTagPickerItem[]> => {
    // Only prepopulate if a case number is provided
    if (!context) {
        return [];
    }
    
    const contacts = await carbonCopyContactPrepopulate(context);
    return contacts.map(adaptContactToTagPickerItem);
};

const CarbonCopyPicker: React.FC<CarbonCopyPickerProps> = ({
    selectedContacts,
    disabled = false,
    onContactsChange,
    label = "Copy to",
    placeholder = "Select contacts to copy",
    caseNumber
}) => {
    // Convert selected contacts to TagPickerItems
    const selectedItems = selectedContacts.map(adaptContactToTagPickerItem);

    // Handle selection changes
    const handleSelectionChange = (items: ContactTagPickerItem[]) => {
        const contacts = items.map(adaptTagPickerItemToContact);
        onContactsChange(contacts);
    };

    return (
        <GenericTagPicker<ContactTagPickerItem, number>
            selectedItems={selectedItems}
            onItemsChange={handleSelectionChange}
            searchFunction={searchContacts}
            prepopulateFunction={prepopulateContacts}
            searchContext={caseNumber}
            label={label}
            placeholder={placeholder}
            disabled={disabled}
            multiSelect={true}
            isContact={true}
        />
    );
};

export default CarbonCopyPicker;
